import React, { useRef, useEffect, useState } from 'react';
import {
  VictoryAxis,
  VictoryChart,
  VictoryErrorBar,
  VictoryScatter,
  VictoryLine,
  VictoryTheme,
} from 'victory';

const SimpleHealthChart = ({ 
  dataType, 
  unit, 
  data, 
  color = "#3B82F6",
  height = 200,
  width = 250,
  showErrorBars = true,
  darkTheme = false
}) => {
  const containerRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(width);

  // Calculate width from container if width is not explicitly provided or is default
  useEffect(() => {
    if (containerRef.current && width === 250) {
      const updateWidth = () => {
        if (containerRef.current) {
          // Get the parent container (chart container with -mx-10)
          const chartContainer = containerRef.current?.parentElement;
          
          if (chartContainer) {
            // Get the grandparent (the container with px-4)
            const parentContainer = chartContainer.parentElement;
            
            if (parentContainer) {
              // Get the parent container's width (this is the content width)
              const parentWidth = parentContainer.clientWidth;
              
              // Get computed margins from the chart container
              const computedStyle = window.getComputedStyle(chartContainer);
              const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
              const marginRight = parseFloat(computedStyle.marginRight) || 0;
              
              // Calculate extended width: parent width + negative margin extensions
              // Negative margins extend the element beyond its parent
              const leftExtension = marginLeft < 0 ? Math.abs(marginLeft) : 0;
              const rightExtension = marginRight < 0 ? Math.abs(marginRight) : 0;
              
              // The chart should use: parent width + extensions
              const extendedWidth = parentWidth + leftExtension + rightExtension;
              
              setChartWidth(extendedWidth);
            } else {
              // Fallback: use viewport width
              setChartWidth(window.innerWidth);
            }
          } else {
            // Fallback: use container width
            const containerWidth = containerRef.current.clientWidth;
            setChartWidth(containerWidth || width);
          }
        } else {
          setChartWidth(width);
        }
      };
      
      // Use requestAnimationFrame for better timing, then setTimeout as fallback
      const rafId = requestAnimationFrame(() => {
        setTimeout(updateWidth, 100);
        updateWidth();
      });
      
      // Set up ResizeObserver to watch for container size changes
      let resizeObserver = null;
      const chartContainer = containerRef.current?.parentElement;
      const parentContainer = chartContainer?.parentElement;
      
      if (parentContainer && window.ResizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          updateWidth();
        });
        resizeObserver.observe(parentContainer);
      }
      
      window.addEventListener('resize', updateWidth);
      
      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener('resize', updateWidth);
        if (resizeObserver && parentContainer) {
          resizeObserver.unobserve(parentContainer);
          resizeObserver.disconnect();
        }
      };
    } else {
      setChartWidth(width);
    }
  }, [width, data]);
  // Transform the data object into Victory chart format
  const transformData = () => {
    if (!data || typeof data !== 'object') return [];
    
    // Get all dates and sort them
    const dates = Object.keys(data).sort();
    const chartData = [];
    
    dates.forEach((dateStr, index) => {
      const dateData = data[dateStr];
      const date = new Date(dateStr);
      
      if (dateData && dateData.avg !== null && dateData.avg !== undefined) {
        // Data exists for this date
        const dataPoint = {
          x: index + 1,
          y: parseFloat(dateData.avg),
          min: dateData.min !== null && dateData.min !== undefined ? parseFloat(dateData.min) : null,
          max: dateData.max !== null && dateData.max !== undefined ? parseFloat(dateData.max) : null,
          hasData: true,
          date: date
        };
        chartData.push(dataPoint);
      } else {
        // No data for this date - create empty data point
        chartData.push({
          x: index + 1,
          y: null,
          min: null,
          max: null,
          hasData: false,
          date: date
        });
      }
    });
    
    return chartData;
  };

  const chartData = transformData();
  
  // Get date labels for x-axis
  const dateLabels = Object.keys(data || {}).sort().map(dateStr => {
    const date = new Date(dateStr);
    return `${date.getDate()}`;
  });

  // Format y-axis labels without units
  const formatYAxis = (t) => {
    if (unit === 'steps') {
      return `${(t/1000).toFixed(0)}k`;
    } else {
      return `${t}`;
    }
  };

  if (chartData.length === 0) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="text-center text-gray-500">
          <p>No data available</p>
        </div>
      </div>
    );
  }

  // Calculate dynamic Y-axis domain and ticks for heartrate
  const calculateHeartrateDomain = () => {
    if (dataType !== 'heartrate') return null;
    
    const heartrateTicks = [20, 40, 60, 80, 100, 120, 140];
    
    // Find min and max values from data (including error bars)
    const dataWithValues = chartData.filter(d => d.hasData);
    if (dataWithValues.length === 0) {
      return { domain: [20, 140], ticks: heartrateTicks };
    }
    
    let dataMin = Infinity;
    let dataMax = -Infinity;
    
    dataWithValues.forEach(point => {
      // Check average value
      if (point.y !== null && point.y !== undefined) {
        dataMin = Math.min(dataMin, point.y);
        dataMax = Math.max(dataMax, point.y);
      }
      // Check min/max from error bars
      if (point.min !== null && point.min !== undefined) {
        dataMin = Math.min(dataMin, point.min);
      }
      if (point.max !== null && point.max !== undefined) {
        dataMax = Math.max(dataMax, point.max);
      }
    });
    
    // Find the next tick below the min (the largest tick that is < dataMin)
    let domainMin = heartrateTicks[0]; // Default to first tick
    for (let i = heartrateTicks.length - 1; i >= 0; i--) {
      if (heartrateTicks[i] < dataMin) {
        domainMin = heartrateTicks[i];
        break;
      }
    }
    // If dataMin is <= the first tick, use first tick
    if (dataMin <= heartrateTicks[0]) {
      domainMin = heartrateTicks[0];
    }
    
    // Find the next tick above the max (the smallest tick that is > dataMax)
    let domainMax = heartrateTicks[heartrateTicks.length - 1]; // Default to last tick
    for (let i = 0; i < heartrateTicks.length; i++) {
      if (heartrateTicks[i] > dataMax) {
        domainMax = heartrateTicks[i];
        break;
      }
    }
    // If dataMax is >= the last tick, use last tick
    if (dataMax >= heartrateTicks[heartrateTicks.length - 1]) {
      domainMax = heartrateTicks[heartrateTicks.length - 1];
    }
    
    // Get ticks within the domain range
    const visibleTicks = heartrateTicks.filter(tick => tick >= domainMin && tick <= domainMax);
    
    return { domain: [domainMin, domainMax], ticks: visibleTicks };
  };

  const heartrateDomain = calculateHeartrateDomain();

  // Calculate dynamic Y-axis domain and ticks for steps
  const calculateStepsDomain = () => {
    if (dataType !== 'step') return null;
    
    const stepsTicks = [0, 2000, 4000, 6000, 8000, 10000, 12000, 14000, 16000];
    
    // Find min and max values from data (including error bars)
    const dataWithValues = chartData.filter(d => d.hasData);
    if (dataWithValues.length === 0) {
      return { domain: [0, 16000], ticks: stepsTicks };
    }
    
    let dataMin = Infinity;
    let dataMax = -Infinity;
    
    dataWithValues.forEach(point => {
      // Check average value
      if (point.y !== null && point.y !== undefined) {
        dataMin = Math.min(dataMin, point.y);
        dataMax = Math.max(dataMax, point.y);
      }
      // Check min/max from error bars
      if (point.min !== null && point.min !== undefined) {
        dataMin = Math.min(dataMin, point.min);
      }
      if (point.max !== null && point.max !== undefined) {
        dataMax = Math.max(dataMax, point.max);
      }
    });
    
    // Find the next tick below the min (the largest tick that is < dataMin)
    let domainMin = stepsTicks[0]; // Default to first tick
    for (let i = stepsTicks.length - 1; i >= 0; i--) {
      if (stepsTicks[i] < dataMin) {
        domainMin = stepsTicks[i];
        break;
      }
    }
    // If dataMin is <= the first tick, use first tick
    if (dataMin <= stepsTicks[0]) {
      domainMin = stepsTicks[0];
    }
    
    // Find the next tick above the max (the smallest tick that is > dataMax)
    let domainMax = stepsTicks[stepsTicks.length - 1]; // Default to last tick
    for (let i = 0; i < stepsTicks.length; i++) {
      if (stepsTicks[i] > dataMax) {
        domainMax = stepsTicks[i];
        break;
      }
    }
    // If dataMax is >= the last tick, use last tick
    if (dataMax >= stepsTicks[stepsTicks.length - 1]) {
      domainMax = stepsTicks[stepsTicks.length - 1];
    }
    
    // Get ticks within the domain range
    const visibleTicks = stepsTicks.filter(tick => tick >= domainMin && tick <= domainMax);
    
    return { domain: [domainMin, domainMax], ticks: visibleTicks };
  };

  const stepsDomain = calculateStepsDomain();

  // Dark theme colors
  const axisColor = darkTheme ? '#8B949E' : '#374151';
  const gridColor = darkTheme ? '#21262D' : '#E5E7EB'; // Very pale gray, barely visible on dark background
  const tickLabelColor = darkTheme ? '#F0F6FC' : '#374151';

  // Determine domain and tick values
  const yDomain = heartrateDomain 
    ? heartrateDomain.domain 
    : stepsDomain
    ? stepsDomain.domain
    : (dataType === 'hrv' ? [0, 100] :
       [0, 100]);
  
  const yTickValues = heartrateDomain 
    ? heartrateDomain.ticks 
    : stepsDomain
    ? stepsDomain.ticks
    : undefined;

  return (
    <div ref={containerRef} className="w-full h-full" style={{ marginLeft: '35px' }}>
      <VictoryChart
        theme={VictoryTheme.material}
        domainPadding={{ x: [0, 0], y: 0 }}
        height={height}
        width={chartWidth}
        padding={{ top: 20, bottom: 40, left: 40, right: 12 }}
        domain={{
          y: yDomain
        }}
        style={{
          background: { fill: 'transparent' }
        }}
      >
        {/* X-Axis */}
        <VictoryAxis
          dependentAxis={false}
          tickFormat={(t) => {
            return dateLabels[t - 1] || '';
          }}
          style={{
            axis: { stroke: axisColor, strokeWidth: 1 },
            tickLabels: { fontSize: 18, padding: 0, fontWeight: 'bold', fill: tickLabelColor },
            grid: { stroke: gridColor, strokeWidth: 1, strokeDasharray: '4,4' }
          }}
        />
        
        {/* Y-Axis */}
        <VictoryAxis
          dependentAxis={true}
          tickFormat={formatYAxis}
          tickValues={yTickValues}
          style={{
            axis: { stroke: axisColor, strokeWidth: 1 },
            tickLabels: { fontSize: 18, padding: 5, fontWeight: 'bold', fill: tickLabelColor },
            grid: { stroke: '#8B949E', strokeWidth: 0.5, strokeDasharray: 'none' }
          }}
        />
        
        {/* Line Chart */}
        <VictoryLine
          data={chartData.filter(d => d.hasData)}
          style={{
            data: { stroke: color, strokeWidth: 2 }
          }}
        />
        
        {/* Scatter Points */}
        <VictoryScatter
          data={chartData.filter(d => d.hasData)}
          size={4}
          style={{
            data: { 
              fill: '#FFFFFF', // White fill inside
              stroke: color, // Colored outline
              strokeWidth: 2
            }
          }}
        />
        
        {/* Error Bars - Using VictoryErrorBar with center point */}
        {showErrorBars && (
          (() => {
            const errorData = chartData.filter(d => d.hasData && d.min !== null && d.max !== null);
            
            // Create data with center point as y value
            const errorBarData = errorData.map(datum => ({
              x: datum.x,
              y: (datum.min + datum.max) / 2, // Center point
              min: datum.min,
              max: datum.max,
              errorY: (datum.max - datum.min) / 2 // Half range
            }));
            
            return (
              <VictoryErrorBar
                data={errorBarData}
                x="x"
                y="y"
                errorX={0}
                errorY="errorY"
                style={{
                  data: { 
                    stroke: color, 
                    strokeWidth: 2,
                    strokeLinecap: "round"
                  }
                }}
              />
            );
          })()
        )}
      </VictoryChart>
    </div>
  );
};

export default SimpleHealthChart; 